import React, { useEffect, useState } from 'react';
import './css/index.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import useInputState from '../../helpers/hooks/useInputState';
import PageTitle from '../../components/PageTitle';
import MyButton from '../../components/MyButton';
import SearchInput from '../../components/SearchInput';
import SelectButton from '../../components/SelectButton';
import SelectCity from './components/SelectCity';
import ContactsSection from './components/ContactsSection';
import SelectPhotos from './components/SelectPhotos';
import checkAddEventPayload from '../../helpers/checkAddEventPayload';
import MyModal from '../../components/MyModal';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import EmptyHeight from '../../components/EmptyHeight';
import isValidDate from '../../helpers/isValidDate';
import MapView from '../AddOrganization/components/MapView';

const tempComponentStates = {
  city: 'city',
  photos: 'photos',
};

function AddEvent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('id');
  const isEditing = Boolean(eventId);

  const [gettingLoading, setGettingLoading] = useState(isEditing);
  const [gettingError, setGettingError] = useState('');

  const [ownerId, setOwnerId] = useInputState();
  const [name, setName] = useInputState();
  const [specialization, setSpecialization] = useInputState();
  const [holdingDate, setHoldingDate] = useInputState();
  const [dateStart, setDateStart] = useInputState();
  const [dateEnd, setDateEnd] = useInputState();
  const [city, setCity] = useState(null);
  const [address, setAddress] = useInputState();
  const [coordinates, setCoordinates] = useState(null);
  const [contacts, setContacts] = useState({});
  const [description, setDescription] = useInputState();
  const [photos, setPhotos] = useState([]);

  const [tempComponentState, setTempComponentState] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalState, setModalState] = useState('');
  const [loading, setLoading] = useState(false);

  const [dropdownAddresses, setDropdownAddresses] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const onCancelClick = () => {
    navigate('/events/view');
  };

  const onSave = async () => {
    const { errors, formData, payload } = checkAddEventPayload({
      owner_id: ownerId,
      name,
      country_id: city?.country_id,
      city_id: city?._id,
      specialization,
      holding_date: holdingDate,
      date_start: dateStart,
      date_end: dateEnd,
      address,
      coordinates,
      contacts,
      description,
      photos,
      withPhotos: !isEditing,
    });

    if (errors?.length) {
      setModalText(`В следующих полях есть ошибки:\n\n${errors.join('\n\n')}`);
      setIsModalVisible(true);
      setModalState('error');
      return;
    }

    if (!isEditing) {
      const data = await sendRequest({
        request: Api.createEvent,
        payload: formData,
        warnErrorText: 'while creating event',
        setLoading,
      });

      setIsModalVisible(true);
      if (data?.status !== 'ok') {
        const errorText = data?.message || 'Не удалось установить соединение с сервером.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      } else {
        setModalText(`Мероприятие "${name}" успешно было создано!`);
        setModalState('success');
      }
    } else {
      const data = await sendRequest({
        request: Api.editEvent,
        payload: { payload, id: eventId },
        warnErrorText: 'while editing event',
        setLoading,
      });

      setIsModalVisible(true);
      if (data?.status !== 'ok') {
        const errorText = data?.message || 'Не удалось установить соединение с сервером.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      } else {
        setModalText(`Мероприятие "${name}" успешно было редактировано!`);
        setModalState('success');
      }
    }
  };

  const onCloseModal = () => {
    if (modalState === 'error') {
      setIsModalVisible(false);
      setTimeout(() => {
        setModalText('');
        setModalState('');
      }, 400);
    } else if (modalState === 'success') {
      setIsModalVisible(false);
      navigate('/events/view');
    }
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getEvent,
      payload: { id: eventId },
      warnErrorText: 'while getting single event',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    const selectedEvent = data.event;
    setOwnerId(selectedEvent.user._id);
    setName(selectedEvent.name);
    setSpecialization(selectedEvent.specialization);
    setHoldingDate(selectedEvent.holding_date);
    setDateStart(isValidDate(new Date(selectedEvent.date_start)) ? moment(selectedEvent.date_start).format('YYYY-MM-DD') : '');
    setDateEnd(isValidDate(new Date(selectedEvent.date_end)) ? moment(selectedEvent.date_end).format('YYYY-MM-DD') : '');
    setAddress(selectedEvent.address);
    setDescription(selectedEvent.description || '');

    delete selectedEvent.contacts.__v;
    delete selectedEvent.contacts._id;
    setContacts(selectedEvent.contacts);

    setCity(selectedEvent.city);
    setTimeout(() => {
      setCoordinates(selectedEvent.coordinates);
    }, 300);
  };

  const onAddressPress = async (item) => {
    setAddress(item.address_main);
    const data = await sendRequest({
      request: Api.getAddressCoordinates,
      payload: { params: { place_id: item.place_id } },
    });

    if (data?.coordinates) {
      setCoordinates(data.coordinates);
    }
  };

  const onMapPress = async (cords) => {
    const data = await sendRequest({
      request: Api.getPlaceNameWithCoordinates,
      payload: { params: cords },
    });

    if (data?.place_name) {
      setAddress(data.place_name);
    }
  };

  const getDropdownAddresses = async ({ signal, addressVal, cityId }) => {
    const data = await sendRequest({
      request: Api.getDropdownAddresses,
      payload: { params: { address: addressVal, city_id: cityId }, signal },
      shouldLogError: false,
      setLoading: setDropdownLoading,
    });

    if (data?.status !== 'ok') {
      return setDropdownAddresses([]);
    }

    setDropdownAddresses(data.addresses);
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (!address?.length || !city?._id) {
      setDropdownAddresses([]);
    } else {
      getDropdownAddresses({
        signal,
        addressVal: address,
        cityId: city._id,
      }).then();
    }

    return () => controller.abort({ response: { data: 'Aborted' } });
  }, [address, city]);

  useEffect(() => {
    if (isEditing) {
      initData().then();
    }
  }, [isEditing]);

  return (
    <div className="add_event">
      <PageTitle
        text={isEditing ? 'Редактировать мероприятие' : 'Добавить мероприятие'}
        RightComponent={(
          <div className="add_event_button_group">
            <MyButton
              text="Отменить"
              onClick={onCancelClick}
              className="add_event_button_group_cancel"
              secondary
            />
            {!gettingError && (
            <MyButton
              text="Сохранить"
              onClick={onSave}
              className="add_event_button_group_save"
              loading={loading}
            />
            )}
          </div>
        )}
      />
      {gettingLoading
        ? <p className="add_event_info_text">Загрузка</p>
        : gettingError
          ? <p className="add_event_info_text">{gettingError}</p>
          : (
            <div className="add_event_container">
              <SearchInput
                wrapperClassName="add_event_container_form_input"
                placeholder="ID владельца"
                withLeftIcon={false}
                value={ownerId}
                onChange={setOwnerId}
                shouldValueConsiderAsFocus={false}
              />

              <SearchInput
                wrapperClassName="add_event_container_form_input"
                placeholder="Название"
                withLeftIcon={false}
                value={name}
                onChange={setName}
                shouldValueConsiderAsFocus={false}
              />

              <SearchInput
                wrapperClassName="add_event_container_form_input"
                placeholder="Дата проведения"
                withLeftIcon={false}
                value={holdingDate}
                onChange={setHoldingDate}
                shouldValueConsiderAsFocus={false}
              />
              <p className="add_event_container_form_label">можно указывать в любом формате, это поле только отображается и ни на что не влияет</p>

              <SearchInput
                wrapperClassName="add_event_container_form_input"
                placeholder="Дата начала"
                withLeftIcon={false}
                value={dateStart}
                onChange={setDateStart}
                shouldValueConsiderAsFocus={false}
                type="date"
                withCloseIcon={false}
              />
              <p className="add_event_container_form_label">Дата начала. Влияет только на сортировку мероприятий в приложении (оставьте пустым, чтоб мероприятие не сортировалось и было сверху)</p>

              <SearchInput
                wrapperClassName="add_event_container_form_input"
                labe="Дата завершения"
                withLeftIcon={false}
                value={dateEnd}
                onChange={setDateEnd}
                shouldValueConsiderAsFocus={false}
                type="date"
                withCloseIcon={false}
              />
              <p className="add_event_container_form_label">Дата завершения. Влияет только на проверку, истекло ли время проведения мероприятия (оставьте пустым, чтоб мероприятие никогда не уходило в неактивные)</p>
              {/* {canHoldingDateBeEdited ? (
                <>

                  <SearchInput
                    wrapperClassName="add_event_container_form_input"
                    placeholder="Дата завершения"
                    withLeftIcon={false}
                    value={holdingDateEnd}
                    onChange={setHoldingDateEnd}
                    shouldValueConsiderAsFocus={false}
                  />
                  <p className="add_event_container_form_label">необязательно. можно указывать в любом формате, это поле только отображается</p>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '13px' }}>Дата (не редактируется из-за несовместимости формата):</p>
                  <EmptyHeight height={8} />
                  <p>{holdingDate}</p>
                </>
              )} */}

              <EmptyHeight height={16} />
              <SearchInput
                wrapperClassName="add_event_container_form_input"
                placeholder="Специализация"
                withLeftIcon={false}
                value={specialization}
                onChange={setSpecialization}
                shouldValueConsiderAsFocus={false}
              />
              <SelectButton
                label="Город"
                value={city?.name}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.city}
                isActive={tempComponentState === tempComponentStates.city}
              />
              {city?._id && (
                <SearchInput
                  wrapperClassName="add_event_container_form_input"
                  placeholder="Адрес"
                  withLeftIcon={false}
                  value={address}
                  onChange={setAddress}
                  shouldValueConsiderAsFocus={false}
                  withDropdown
                  dropdownItems={dropdownAddresses}
                  dropdownLabelField="address_full"
                  dropdownKeyField="place_id"
                  onDropdownItemPress={onAddressPress}
                  dropdownLoading={dropdownLoading}
                />
              )}
              <MapView
                city={city}
                coordinates={coordinates}
                setCoordinates={setCoordinates}
                onMapPress={onMapPress}
              />
              <ContactsSection
                contacts={contacts}
                setContacts={setContacts}
              />
              <SearchInput
                wrapperClassName="add_event_container_descr"
                withLeftIcon={false}
                placeholder="Описание"
                value={description}
                onChange={setDescription}
                isLarge
                shouldValueConsiderAsFocus={false}
              />
              {!isEditing && (
              <SelectButton
                label="Добавить фото"
                value={photos.length ? `${photos.length} фото` : ''}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.photos}
                isActive={tempComponentState === tempComponentStates.photos}
              />
              )}
            </div>
          )}

      {tempComponentState && (
      <div className="add_event_temp_container">
        {tempComponentState === tempComponentStates.city ? (
          <SelectCity
            setValue={setCity}
            setTempComponentState={setTempComponentState}
          />
        ) : tempComponentState === tempComponentStates.photos ? (
          <SelectPhotos
            setValue={setPhotos}
            setTempComponentState={setTempComponentState}
            initValue={photos}
          />
        ) : null}
      </div>
      )}

      <MyModal isOpen={isModalVisible} closeFunction={onCloseModal}>
        <div className="add_event_modal">
          <pre>{modalText}</pre>

          <MyButton
            className="add_event_modal_button"
            text="Ок"
            onClick={onCloseModal}
          />
        </div>
      </MyModal>
    </div>

  );
}

export default AddEvent;
